// Scene renderer (presentation-2 contract sections 2 and 7): draws scene.json as a CSS grid of named
// areas on a material backdrop, with the client's own overlays (action bar, moves drawer, turn
// banner, popovers). Zones no brick places land in the `rest` area, so nothing is hidden.

import { PackageUiHost } from '../package-ui/PackageUiHost';
import { logLine, readableLog } from './logText';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Session, SessionState } from '../session/Session';
import { buildTable, type TableModel } from '../model/table';
import { buildChoices } from '../model/choices';
import type { BrickInstance, Presentation, PresentationV2, Scene } from '../presentation/types';
import { PresentationContext, createPresentationApi } from '../presentation/PresentationContext';
import { seatColorVar } from '../components/seatColors';
import { themeCssV2 } from '../theme/themeV2';
import { PlayController } from '../play/PlayController';
import { PlayOverlays, TurnBanner, UndoButton } from '../play/Overlays';
import { BrickHost } from '../bricks/host';
import { SceneProvider, placedZones, type SceneApi } from './SceneContext';
import { MotionFlagsProvider, useDirector } from '../motion/MotionLayer';
import { Table } from '../components/Table';
import { ReplayBanner, ReplayOverlay, ReplayProvider, ViewArea, ViewsProvider, type ReplayApi } from './SceneViews';
import { replayMarks, replayStop } from '../motion/replay';
import { promptPlace } from '../presentation/validate2';
import { useReportGameView } from '../issues/IssueContext';
import { GameSettings, useGameDisplay } from '../display/GameDisplay';
import { spectatorScene } from '../display/spectatorScene';
import { ArtImage } from '../art/ArtImage';
import { enableRailDragScroll, revealFirstActiveTool } from './railScroll';
import './compact-table.css';

const SPEED_KEY = 'bgc.opponentPace';

function useWidth(): number {
  const [w, setW] = useState(() => (typeof window === 'undefined' ? 1440 : window.innerWidth));
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return w;
}

function readPace(fallback: number): number {
  try { const v = Number(window.localStorage.getItem(SPEED_KEY)); return [0, 0.5, 1, 2].includes(v) && window.localStorage.getItem(SPEED_KEY) !== null ? v : fallback; } catch { return fallback; }
}

interface Props { session: Session; state: SessionState; presentation: PresentationV2; fallbackTable: TableModel }

export function SceneTable({ session, state, presentation, fallbackTable }: Props) {
  const boundary = useRef({ session, matchId: state.matchId, seat: state.seat, epoch: 0 });
  if (boundary.current.session !== session || boundary.current.matchId !== state.matchId || boundary.current.seat !== state.seat) boundary.current = { session, matchId: state.matchId, seat: state.seat, epoch: boundary.current.epoch + 1 };
  const width = useWidth();
  const display = useGameDisplay();
  const spectatorBoard = !!display?.spectator && display.boardCapable && display.spectatorBoard;
  const file = presentation.scene;
  const wide = file.scene;
  const alternate = useMemo(() => spectatorBoard ? spectatorScene(presentation, width) : null, [spectatorBoard, presentation, width]);
  // A table with one seat may have its own wide scene (`solo`): the same grid with the room a lone player does not share.
  const solo = file.solo && state.observation?.ui.seats?.length === 1 ? file.solo : null;
  const scene: Scene | null = alternate ?? (width >= (wide.minWidth ?? 0) ? solo ?? wide : file.narrow ?? null);
  if (!scene) {
    // Below minWidth without a narrow scene: the v1 layouts if present, else the generic layout.
    const v1: Presentation | null = presentation.layouts ? {
      manifest: presentation.manifest as never, layouts: presentation.layouts, templates: presentation.templates,
      interactions: presentation.interactions, theme: presentation.theme, assetUrls: presentation.assetUrls,
    } : null;
    const choices = state.observation ? buildChoices(state.observation, state.actionSchemas, new Set(fallbackTable.objects.keys())) : [];
    return <Table session={session} state={state} table={fallbackTable} choices={choices} presentation={v1} presentationNotice={null} />;
  }
  return <SceneView key={boundary.current.epoch} session={session} state={state} presentation={presentation} scene={scene} narrow={!spectatorBoard && scene !== wide && scene !== solo} spectatorBoard={spectatorBoard} />;
}

function SceneView({ session, state, presentation, scene, narrow, spectatorBoard }: { session: Session; state: SessionState; presentation: PresentationV2; scene: Scene; narrow: boolean; spectatorBoard: boolean }) {
  const display = useGameDisplay();
  const root = useRef<HTMLDivElement | null>(null);
  const layer = useRef<HTMLDivElement | null>(null);
  const [pace, setPaceState] = useState(() => readPace(presentation.theme.motion?.opponentPace ?? 1));
  const [replayRev, setReplayRev] = useState<number | null>(null);
  const [promptEl, setPromptEl] = useState<HTMLElement | null>(null);
  const [mobilePanel, setMobilePanel] = useState<'board' | 'hand' | 'tools' | 'log'>('board');
  const [turnCollapsed, setTurnCollapsed] = useState(false);
  const [turnHeight, setTurnHeight] = useState(32);
  useEffect(() => setTurnCollapsed(false), [state.decisionId]);
  const [motionLimit,setMotionLimit]=useState<number|undefined>(undefined);
  const headerPrompt = promptPlace(scene) === 'header' && Object.values(scene.place).flat().some(b => b.brick === 'hud');
  const setPace = useCallback((p: number) => { setPaceState(p); try { window.localStorage.setItem(SPEED_KEY, String(p)); } catch { /* per-viewer convenience only */ } }, []);

  const seatIds = useMemo(() => (state.observation?.ui.seats ?? []).map(s => s.id), [state.observation]);
  const seatColor = useCallback((seat: string | null) => {
    if (!seat) return 'var(--ink-muted)';
    const i = seatIds.indexOf(seat);
    return presentation.theme.seatColors?.[seat] ?? seatColorVar(i < 0 ? 0 : i);
  }, [seatIds, presentation]);
  const segments = useCallback((id: string) => {
    const ref = state.observation?.ui.objects?.find(o => o.id === id)?.ref;
    const edge = ref ? state.description?.boards.flatMap(b => b.edges).find(e => e.id === ref) : undefined;
    return edge?.segments ?? 1;
  }, [state.observation, state.description]);

  const director = useDirector(state, root, layer, {
    self: state.seat && state.seat !== 'spectator' ? state.seat : null, description: state.description,
    speed: presentation.theme.motion?.speed ?? 'brisk', pace, segments, seatColor, paused: replayRev !== null,
    maximumMotionMs:motionLimit,
    formatLog: message => logLine(message, state.observation?.ui.seats ?? [], presentation.theme.maps?.log),
  });
  const live = director.shown;
  // Move replay: a buffered past position replaces the live one on the table (input disabled).
  const stop = useMemo(() => (replayRev !== null ? replayStop(state.history, replayRev) : null), [replayRev, state.history]);
  const shown = useMemo(() => (stop && live ? { observation: stop.frame.observation, log: live.log, revision: stop.frame.revision } : live), [stop, live]);
  useEffect(() => {
    if (scene.shell !== 'compact-table' || !shown || !root.current) return;
    return enableRailDragScroll(root.current);
  }, [scene.shell, !!shown]);
  useEffect(() => {
    if (scene.shell !== 'compact-table' || !shown || !root.current) return;
    const frame = requestAnimationFrame(() => { if (root.current) revealFirstActiveTool(root.current); });
    return () => cancelAnimationFrame(frame);
  }, [scene.shell, shown?.revision]);
  useReportGameView(state, shown?.observation ?? null, shown?.revision ?? state.revision, presentation);
  const table = useMemo(() => (shown ? buildTable(shown.observation, state.description, state.seat, { matchId: state.matchId, profiles: state.profiles }) : null), [shown, state.description, state.seat, state.matchId, state.profiles]);
  useLayoutEffect(() => {
    if (scene.shell !== 'compact-table' || !table) return;
    const hud = root.current?.querySelector<HTMLElement>('.scene-area-turn .hud');
    if (!hud || typeof ResizeObserver === 'undefined') return;
    const measure = () => setTurnHeight(Math.ceil(hud.scrollHeight));
    const observer = new ResizeObserver(measure);
    observer.observe(hud);
    measure();
    return () => observer.disconnect();
  }, [scene, table]);
  const choices = useMemo(() => (shown && table ? buildChoices(shown.observation, state.actionSchemas, new Set(table.objects.keys())) : []), [shown, table, state.actionSchemas]);

  const seatColorRaw = useCallback((owner: string) => seatColor(owner), [seatColor]);
  const api = useMemo(() => (table ? createPresentationApi(presentation, table, seatColorRaw, null) : null), [presentation, table, seatColorRaw]);
  const css = useMemo(() => themeCssV2(presentation.theme, seatIds), [presentation, seatIds]);

  // Readable lines (theme map `log`) for the log, opponent strips and turn banners alike.
  const rawLog = shown?.log ?? state.log;
  const log = useMemo(() => (table ? readableLog(rawLog, table.seats, presentation.theme.maps?.log) : rawLog), [rawLog, table, presentation]);
  const lastAction = useMemo(() => {
    const map = new Map<string, string>();
    if (!table) return map;
    for (const entry of log) for (const s of table.seats) if (entry.message.includes(s.label)) map.set(s.id, entry.message);
    return map;
  }, [log, table]);

  const marks = useMemo(() => {
    if (!stop?.prev || !table) return null;
    const before = buildTable(stop.prev.observation, state.description, state.seat, { matchId: state.matchId, profiles: state.profiles });
    return replayMarks(before, table, stop.frame.events.map(e => e.message));
  }, [stop, table, state.description, state.seat, state.matchId]);
  const replay = useMemo<ReplayApi>(() => ({
    stop, marks,
    enter: revision => setReplayRev(revision),
    exit: () => setReplayRev(null),
  }), [stop, marks]);

  const sceneApi: SceneApi | null = useMemo(() => (table ? {
    table, presentation, seatColor, log, lastAction, speed: pace, setSpeed: setPace, setMotionLimit, activitySuspended: !!stop || state.connection !== 'open',
    headerPrompt, promptHost: setPromptEl, banner: scene.views ? director.banner : null,
    seatLabel: seat => (seat ? table.seats.find(s => s.id === seat)?.label ?? seat : ''),
    board: id => {
      const zone = table.zones.find(z => z.kind === 'board' && z.boardId === id);
      const board = table.boards.find(b => b.id === id);
      return zone && board ? { zone, board } : null;
    },
  } : null), [table, presentation, seatColor, log, lastAction, pace, setPace, headerPrompt, scene.views, director.banner, stop, state.connection]);

  // Tab bricks place their zones too (perSeat tabs as a repeat over opponents).
  const instances = useMemo(() => [
    ...Object.values(scene.place).flat(),
    ...(scene.views?.tabs ?? []).flatMap(t => t.perSeat === 'opponents' ? t.bricks.map((child): BrickInstance => ({ brick: 'repeat', over: 'opponents', child })) : t.bricks),
  ], [scene]);
  const inlineInspector = instances.some(inst => inst.brick === 'object-inspector');
  const rest = useMemo(() => {
    if (!table) return [];
    const placed = placedZones(instances, table);
    const omitted = new Set(scene.omitBoards ?? []);
    return table.zones.filter(z => !placed.has(z.id) && !(z.kind === 'board' && z.boardId && omitted.has(z.boardId))).map((z): BrickInstance => (
      z.kind === 'board' && z.boardId ? { brick: 'route-map', select: `board:${z.boardId}` }
        : z.kind === 'stack' || z.kind === 'discard' ? { brick: 'pile', select: `zone:${z.id}` }
          : { brick: 'card-grid', select: `zone:${z.id}`, props: { size: 's' } }
    ));
  }, [instances, table, scene]);
  const views = useMemo(() => spectatorBoard && scene.views ? { ...scene.views, tabs: scene.views.tabs.map(t => t.id === 'spectator-public' ? { ...t, bricks: rest } : t) } : scene.views, [spectatorBoard, scene.views, rest]);

  // Toasts sit under the header (prompt bar included), wherever its wrapped height ends: `--hud-bottom` on the scene root.
  const ready = !!table && !!shown;
  useLayoutEffect(() => {
    const el = root.current;
    const hud = el?.querySelector<HTMLElement>('.scene-area-hud');
    if (!el || !hud || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => { el.style.setProperty('--hud-bottom', `${Math.round(hud.getBoundingClientRect().bottom - el.getBoundingClientRect().top)}px`); };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(hud); observer.observe(el);
    return () => { observer.disconnect(); el.style.removeProperty('--hud-bottom'); };
  }, [ready, scene]);

  if (!table || !api || !sceneApi || !shown) {
    return <main className="screen-message" aria-busy="true"><div className="spinner" aria-hidden="true" /><p>Setting the table…</p></main>;
  }
  const areas = scene.areas.map(a => `"${a}"`).join(' ');
  const rows = [...scene.rows];
  if (scene.shell === 'compact-table') {
    const turnRow = scene.areas.findIndex(row => row.trim().split(/\s+/).includes('turn'));
    if (turnRow >= 0) rows[turnRow] = turnCollapsed ? `${narrow ? 30 : 22}px` : `${Math.max(Number.parseInt(scene.rows[turnRow]!, 10) || 0, turnHeight)}px`;
  }
  const areaNames = [...new Set([...Object.keys(scene.place), ...(scene.place[scene.rest] ? [] : [scene.rest]), ...(scene.views && !scene.place[scene.views.area] ? [scene.views.area] : [])])];
  return (
    <PresentationContext.Provider value={api}>
      <style>{css}</style>
      <SceneProvider value={sceneApi}>
        <MotionFlagsProvider value={director.flags}>
          <PlayController session={session} state={state} table={table} choices={choices} rules={presentation.targets.targets}
            quickSubmit={presentation.interactions.quickSubmit ?? false} preferFree={presentation.interactions.preferFree ?? false} listPicks={!!presentation.interactions.pickLists} forms={presentation.interactions.forms} autoAnswer={presentation.interactions.autoAnswer} revision={shown.revision} stale={!!stop || shown.observation !== state.observation}>
            <ReplayProvider value={replay}>
            <ViewsProvider views={views}>
            <div ref={root} className={`scene-root${presentation.scene.guide?' has-catalog-guide':''}${spectatorBoard ? ' is-spectator-board' : ''}${narrow ? ' is-narrow' : ''}${scene.shell === 'compact-table' ? ' is-compact-table' : ''}${scene.flow === 'page' ? ' is-flow' : ''}${scene.touch ? ' is-touch' : ''}${table.finished ? ' is-finished' : ''}${stop ? ' is-replaying' : ''}${headerPrompt ? ' has-header-prompt' : ''}${scene.views ? ' has-views' : ''}${scene.promptTone === 'dark' ? ' has-dark-prompt' : ''}${scene.toast === 'corner' ? ' has-corner-toast' : ''}`} data-connection={state.connection} data-mobile-panel={mobilePanel}>
              {state.connection !== 'open' && <p className="scene-connection" role="status">{state.connection === 'reconnecting' ? 'Reconnecting to the game host…' : 'Connecting…'}</p>}
              <PackageUiHost presentation={presentation} observation={shown.observation} description={state.description} seat={state.seat} revision={shown.revision} />
              <div className="scene-grid" style={{ gridTemplateAreas: areas, gridTemplateColumns: scene.columns.join(' '), gridTemplateRows: rows.join(' ') }}>
                {areaNames.map(area => (
                  <div key={area} className={`scene-area scene-area-${area}${scene.views?.area === area ? ' has-views' : ''}${scene.backgrounds?.[area] ? ' has-area-art' : ''}${area === 'turn' && turnCollapsed ? ' is-collapsed' : ''}`} style={{ gridArea: area }}
                    {...(scene.shell === 'compact-table' && (area === 'players' || area === 'side') ? { tabIndex: 0, 'aria-label': area === 'players' ? 'Player rail' : 'Tools and moves rail' } : {})}>
                    {scene.backgrounds?.[area] && <ArtImage source={scene.backgrounds[area]} className="area-background" />}
                    {area === 'side' && scene.shell === 'compact-table' && narrow && <div className="compact-mobile-settings"><strong>{table.title}</strong><GameSettings pace={pace} setPace={setPace} /></div>}
                    {(scene.place[area] ?? []).map((inst, i) => <BrickHost key={i} inst={inst} seat={null} />)}
                    {area === 'turn' && scene.shell === 'compact-table' && <div className="compact-turn-controls">
                      <button type="button" className="compact-turn-collapse" aria-label={turnCollapsed ? 'Expand turn controls' : 'Collapse turn controls'} aria-expanded={!turnCollapsed} title={turnCollapsed ? 'Expand turn controls' : 'Collapse turn controls'} onClick={() => setTurnCollapsed(value => !value)}>{turnCollapsed ? '▣' : '▤'}</button>
                      <UndoButton session={session} state={state} off={!!stop} />
                      {!narrow && <><button type="button" className={`compact-follow${display?.followTabs && display.following !== 'off' ? ' is-on' : ''}`} aria-pressed={!!display?.followTabs && display.following !== 'off'} aria-label="Follow board actions and tabs" title="Follow board actions and tabs" onClick={() => { if (!display) return; const enable = !(display.followTabs && display.following !== 'off'); display.update('followTabs', enable); display.update('following', enable ? 'significant' : 'off'); }}>◎</button><GameSettings pace={pace} setPace={setPace} /></>}
                    </div>}
                    {scene.views?.area === area && <ViewArea />}
                    {!spectatorBoard && area === scene.rest && rest.map(inst => <BrickHost key={inst.select} inst={inst} seat={null} />)}
                  </div>
                ))}
              </div>
              {scene.shell === 'compact-table' && narrow && <nav className="compact-mobile-nav" aria-label="Table panels">
                {(['board', 'hand', 'log', 'tools'] as const).map(panel => <button key={panel} type="button" aria-pressed={mobilePanel === panel} onClick={() => setMobilePanel(panel)}>{panel[0]!.toUpperCase() + panel.slice(1)}</button>)}
              </nav>}
              {director.banner && !scene.views && <TurnBanner key={director.banner.key} text={director.banner.text} color={director.banner.color} />}
              <PlayOverlays session={session} state={state} table={table} choices={choices} promptHost={headerPrompt ? promptEl : null} replaying={!!stop} compactShell={scene.shell === 'compact-table'} inlineInspector={inlineInspector} />
              {!scene.views && <ReplayBanner />}
              <ReplayOverlay />
              <div ref={layer} className="motion-layer" aria-hidden="true" />
            </div>
            </ViewsProvider>
            </ReplayProvider>
          </PlayController>
        </MotionFlagsProvider>
      </SceneProvider>
    </PresentationContext.Provider>
  );
}

